import React, { useEffect, useRef, useState } from 'react';
import { renderPreview } from '../../lib/ticketPrinter/preview';
import { sampleFace } from '../../lib/ticketPrinter/ticketFace';
import { DEFAULT_TEMPLATE, drawLayout, layoutLength, programmeLedger } from '../../lib/ticketPrinter/templates';

/** A template, drawn from the same lines the printer is sent, at the printer's own geometry. */
const TemplatePreview = ({ profile, ticket }) => {
  const ref = useRef(null);
  const span = profile?.paperWidthDots ?? profile?.headDots ?? 576;
  const width = span - 2 * Math.min(profile?.marginDots ?? 0, Math.floor(span / 4));
  const ops = programmeLedger(ticket ?? sampleFace('Burkina Faso vs Central African Republic'), width, profile);
  useEffect(() => { if (ref.current) drawLayout(ref.current, ops, width, profile); });
  const mm = Math.round(layoutLength(ops) / 8);
  return (
    <div className="shrink-0">
      <div className="text-[10px] text-[#9CA3AF] mb-1 text-center">Preview</div>
      <div className="bg-white border border-[#D9D6D0] shadow-sm" style={{ padding: `8px ${Math.round(((span - width) / 2) * 0.36)}px` }}>
        <canvas ref={ref} style={{ width: Math.round(width * 0.36), display: 'block' }} />
      </div>
      <div className="text-[10px] text-[#9CA3AF] mt-1 text-center">{mm} mm long · {Math.round(width / 8)} mm wide</div>
    </div>
  );
};

/**
 * The ticket as this printer will lay it out — rendered from the real bytes (see preview.ts), on
 * a strip the width of the print area, with the side margins shaded. A change to a margin or the
 * header shows here before anyone spends a ticket finding out.
 */
const CH = 4.6; // px per printer column (12 dots) at this scale

export const TicketPreview = ({ profile, ticket }) =>
  (profile?.template ?? DEFAULT_TEMPLATE) === 'classic' ? <ClassicPreview profile={profile} ticket={ticket} /> : <TemplatePreview profile={profile} ticket={ticket} />;

const ClassicPreview = ({ profile, ticket }) => {
  const [lines, setLines] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      renderPreview(profile, ticket ?? sampleFace('SAMPLE EVENT')).then((r) => { if (!cancelled) setLines(r.lines); }).catch(() => {});
    }, 120);
    return () => { cancelled = true; clearTimeout(t); };
  }, [profile, ticket]);

  const areaDots = profile?.paperWidthDots ?? profile?.headDots ?? 576;
  const leftDots = profile?.paperWidthDots ? profile?.paperLeftDots ?? 0 : 0;
  const cols = Math.floor(areaDots / 12);
  const marginCols = Math.min(Math.floor((profile?.marginDots ?? 0) / 12), Math.floor(cols / 4));
  return (
    <div className="shrink-0">
      <div className="text-[10px] text-[#9CA3AF] mb-1 text-center">Preview</div>
      <div className="relative bg-white border border-[#D9D6D0] shadow-sm overflow-hidden" style={{ width: cols * CH + 8, minHeight: 220 }}>
        {marginCols > 0 && <>
          <div className="absolute inset-y-0 left-1 bg-[repeating-linear-gradient(45deg,#F2F1ED,#F2F1ED_3px,#fff_3px,#fff_6px)]" style={{ width: marginCols * CH }} />
          <div className="absolute inset-y-0 right-1 bg-[repeating-linear-gradient(45deg,#F2F1ED,#F2F1ED_3px,#fff_3px,#fff_6px)]" style={{ width: marginCols * CH }} />
        </>}
        <div className="relative px-1 py-2 font-mono text-[#111] leading-[1.25]" style={{ fontSize: CH / 0.6 }}>
          {!lines && <div className="text-[#9CA3AF] text-center py-10" style={{ fontSize: 10 }}>…</div>}
          {lines?.map((l, i) => {
            if (l.kind === 'qr') return <div key={i} className="mx-auto my-1 bg-[repeating-conic-gradient(#111_0_25%,#fff_0_50%)] bg-[length:6px_6px] border-[3px] border-[#111]" style={{ width: 54, height: 54 }} />;
            if (l.kind === 'barcode') return <div key={i} className="mx-auto my-1 bg-[repeating-linear-gradient(90deg,#111_0_1px,#fff_1px_3px,#111_3px_5px,#fff_5px_6px)]" style={{ width: 90, height: 14 }} />;
            const crop = Math.floor(leftDots / (12 * l.mag));
            return (
              <div key={i} className="whitespace-pre overflow-hidden" style={{ fontSize: (CH / 0.6) * l.mag, fontWeight: l.mag > 1 ? 700 : 400 }}>
                {l.text.slice(crop) || ' '}
              </div>
            );
          })}
        </div>
      </div>
      <div className="text-[10px] text-[#9CA3AF] mt-1 text-center">
        {marginCols > 0 ? `${Math.round((areaDots - 2 * (profile?.marginDots ?? 0)) / 8)} mm of text inside ${Math.round(areaDots / 8)} mm` : `${Math.round(areaDots / 8)} mm print area`}
      </div>
    </div>
  );
};
